import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Grid,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { ChromePicker } from 'react-color';
import AuthLayout from '../components/AuthLayout';
import categoryService, { Category } from '../services/category.service';

const CategoryList: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [formTitle, setFormTitle] = useState('Nova Categoria');
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState<Partial<Category>>({
    name: '',
    description: '',
    color: '#2e7d32',
    isActive: true
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  
  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  
  useEffect(() => {
    fetchCategories();
  }, []);
  
  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await categoryService.getCategories();
      setCategories(response.categories);
    } catch (err: any) {
      console.error('Error fetching categories:', err);
      setError('Falha ao carregar categorias. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleOpenForm = (category?: Category) => {
    if (category) {
      setFormTitle('Editar Categoria');
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        color: category.color,
        isActive: category.isActive
      });
    } else {
      setFormTitle('Nova Categoria');
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        color: '#2e7d32',
        isActive: true
      });
    }
    
    setFormError('');
    setFormOpen(true);
  };
  
  const handleCloseForm = () => {
    setFormOpen(false);
    setFormError('');
  };
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleColorChange = (color: any) => {
    setFormData(prev => ({
      ...prev,
      color: color.hex
    }));
  };
  
  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name) {
      setFormError('O nome da categoria é obrigatório');
      return;
    }
    
    try {
      setFormLoading(true);
      setFormError('');
      
      if (editingCategory) {
        await categoryService.updateCategory(editingCategory._id as string, formData);
      } else {
        await categoryService.createCategory(formData as Omit<Category, '_id' | 'createdAt' | 'updatedAt'>);
      }
      
      // Refresh categories
      await fetchCategories();
      
      // Close form
      setFormOpen(false);
    } catch (err: any) {
      console.error('Error saving category:', err);
      
      if (err.response?.data?.error === 'duplicate_name') {
        setFormError('Já existe uma categoria com este nome');
      } else {
        setFormError('Falha ao salvar categoria. Por favor, tente novamente.');
      }
    } finally {
      setFormLoading(false);
    }
  };
  
  const handleDeleteClick = (category: Category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
    setDeleteError('');
  };
  
  const handleDeleteConfirm = async () => {
    if (!categoryToDelete) return;
    
    try {
      setDeleteLoading(true);
      setDeleteError('');
      
      await categoryService.deleteCategory(categoryToDelete._id as string);
      
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      
      // Refresh categories
      fetchCategories();
    } catch (err: any) {
      console.error('Error deleting category:', err);
      
      if (err.response?.data?.error === 'category_in_use') {
        setDeleteError(`Esta categoria está sendo usada por ${err.response.data.assetCount} ativos e não pode ser excluída.`);
      } else {
        setDeleteError('Falha ao excluir categoria. Por favor, tente novamente.');
      }
    } finally {
      setDeleteLoading(false);
    }
  };
  
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
    setDeleteError('');
  };
  
  return (
    <AuthLayout title="Categorias">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 500 }}>
            Categorias
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm()}
            sx={{ 
              backgroundColor: '#2e7d32',
              '&:hover': {
                backgroundColor: '#1b5e20',
              }
            }}
          >
            Nova Categoria
          </Button>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Cor</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Descrição</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={40} sx={{ color: '#2e7d32' }} />
                    </TableCell>
                  </TableRow>
                ) : categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Nenhuma categoria encontrada
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((category) => (
                    <TableRow hover key={category._id}>
                      <TableCell>
                        <Box 
                          sx={{ 
                            width: 24, 
                            height: 24, 
                            borderRadius: '50%', 
                            bgcolor: category.color,
                            border: '1px solid rgba(0, 0, 0, 0.12)'
                          }} 
                        />
                      </TableCell>
                      <TableCell>{category.name}</TableCell>
                      <TableCell>{category.description || '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={category.isActive ? "Ativo" : "Inativo"} 
                          color={category.isActive ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Editar">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenForm(category)}
                            sx={{ color: '#ff9800' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteClick(category)}
                            sx={{ color: '#f44336' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
      
      {/* Category Form Dialog */}
      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>{formTitle}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmitForm} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Nome"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  margin="normal"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descrição"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  margin="normal"
                  multiline
                  rows={2}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Cor
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box 
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: '50%', 
                      bgcolor: formData.color,
                      border: '1px solid rgba(0, 0, 0, 0.12)'
                    }} 
                  />
                  <ChromePicker 
                    color={formData.color} 
                    onChange={handleColorChange}
                    disableAlpha
                  />
                </Box>
              </Grid>
            </Grid>
            
            {formError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {formError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm} disabled={formLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSubmitForm} 
            variant="contained"
            color="primary"
            disabled={formLoading}
            startIcon={formLoading ? <CircularProgress size={20} /> : null}
            sx={{ 
              backgroundColor: '#2e7d32',
              '&:hover': {
                backgroundColor: '#1b5e20',
              }
            }}
          >
            {formLoading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir a categoria "{categoryToDelete?.name}"?
            Esta ação não pode ser desfeita.
          </DialogContentText>
          {deleteError && (
            <Box sx={{ mt: 2 }}>
              <Typography color="error">{deleteError}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : null}
          >
            {deleteLoading ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </AuthLayout>
  );
};

export default CategoryList;
